import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Row, Col, Button, Form } from "react-bootstrap";
import { Alerts, ButtonLoader } from "Components";
import { unAuthService } from "Service/Axios";
import { CREATE_OTP_URL, REGISTRATION_URL } from "Constant/api";
import { paths } from "Constant/paths";
import { passwordRules } from "Constant/constants";
import { checkPasswordStrength, encryptFun, handleAlphabets } from "Utils";
import { Validator } from "Utils/validator";

export default function CreateAccount() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [isShowPassword, setIsShowPassword] = useState(false);
  const [isShowConfirmPassword, setIsShowConfirmPassword] = useState(false);

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    confirmPassword: "",
    isValid: true,
  });
  const [errMsg, setErrMsg] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    confirmPassword: "",
    isValid: "",
  });
  const [passwordStrength, setPasswordStrength] = useState({
    label: "",
    value: 0,
    errors: [],
  });

  const { verifyEmail } = paths;
  const { firstName, lastName, email, password, confirmPassword, isValid } =
    formData;
  const passwordLength = password.length > 0;

  // Toggle
  const togglePassword = () => {
    setIsShowPassword((prev) => !prev);
  };

  const toggleConfirmPassword = () => {
    setIsShowConfirmPassword((prev) => !prev);
  };

  // On Change
  const handleChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
    if (e.target.name === "firstName") {
      if (!e.target.value) {
        setErrMsg((prev) => ({
          ...prev,
          firstName: t(" common.messages.error.common.firstName.blank"),
        }));
      } else if (e.target.value && !Validator.name(e.target.value)) {
        setErrMsg((prev) => ({
          ...prev,
          firstName: t("common.messages.error.common.firstName.inValid"),
        }));
      } else {
        setErrMsg((prev) => ({
          ...prev,
          firstName: "",
        }));
      }
    }

    if (e.target.name === "lastName") {
      if (!e.target.value) {
        setErrMsg((prev) => ({
          ...prev,
          lastName: t("common.messages.error.common.lastName.blank"),
        }));
      } else if (e.target.value && !Validator.name(e.target.value)) {
        setErrMsg((prev) => ({
          ...prev,
          lastName: t("common.messages.error.common.lastName.inValid"),
        }));
      } else {
        setErrMsg((prev) => ({
          ...prev,
          lastName: "",
        }));
      }
    }

    if (e.target.name === "email") {
      if (!e.target.value) {
        setErrMsg((prev) => ({
          ...prev,
          email: t("common.messages.error.common.email.blank"),
        }));
      } else if (e.target.value && !Validator.email(e.target.value)) {
        setErrMsg((prev) => ({
          ...prev,
          email: t("common.messages.error.common.email.inValid"),
        }));
      } else {
        setErrMsg((prev) => ({
          ...prev,
          email: "",
        }));
      }
    }

    if (e.target.name === "password") {
      setPasswordStrength(
        checkPasswordStrength(e.target.value, firstName, lastName, email)
      );
      if (!e.target.value) {
        setErrMsg((prev) => ({
          ...prev,
          password: t("common.messages.error.common.password.blank"),
        }));
      } else {
        setErrMsg((prev) => ({
          ...prev,
          password: "",
        }));
      }
    }

    if (e.target.name === "confirmPassword") {
      if (!e.target.value) {
        setErrMsg((prev) => ({
          ...prev,
          confirmPassword: t(
            "common.messages.error.common.confirmPassword.blank"
          ),
        }));
      } else if (
        e.target.value &&
        !Validator.confirmPasswordCheck(password, e.target.value)
      ) {
        setErrMsg((prev) => ({
          ...prev,
          confirmPassword: t(
            "common.messages.error.common.confirmPassword.inValid"
          ),
        }));
      } else {
        setErrMsg((prev) => ({
          ...prev,
          confirmPassword: "",
        }));
      }
    }
  };

  // Validation
  const getValidation = () => {
    return new Promise((resolve) => {
      const passwordStrengthResult = checkPasswordStrength(
        password,
        firstName,
        lastName,
        email
      );
      if (
        firstName &&
        Validator.name(firstName) &&
        lastName &&
        Validator.name(lastName) &&
        email &&
        Validator.email(email) &&
        password &&
        passwordStrengthResult.value === 100 &&
        confirmPassword &&
        Validator.confirmPasswordCheck(password, confirmPassword)
      ) {
        resolve(true);
      } else {
        if (!firstName) {
          setErrMsg((prev) => ({
            ...prev,
            firstName: t("common.messages.error.common.firstName.blank"),
          }));
        } else if (firstName && !Validator.name(firstName)) {
          setErrMsg((prev) => ({
            ...prev,
            firstName: t("common.messages.error.common.firstName.inValid"),
          }));
        }

        if (!lastName) {
          setErrMsg((prev) => ({
            ...prev,
            lastName: t("common.messages.error.common.lastName.blank"),
          }));
        } else if (lastName && !Validator.name(lastName)) {
          setErrMsg((prev) => ({
            ...prev,
            lastName: t("common.messages.error.common.lastName.inValid"),
          }));
        }

        if (!email) {
          setErrMsg((prev) => ({
            ...prev,
            email: t("common.messages.error.common.email.blank"),
          }));
        } else if (email && !Validator.email(email)) {
          setErrMsg((prev) => ({
            ...prev,
            email: t("common.messages.error.common.email.inValid"),
          }));
        }

        if (!password) {
          setErrMsg((prev) => ({
            ...prev,
            password: t("common.messages.error.common.password.blank"),
          }));
        }

        if (!confirmPassword) {
          setErrMsg((prev) => ({
            ...prev,
            confirmPassword: t(
              "common.messages.error.common.confirmPassword.blank"
            ),
          }));
        } else if (
          confirmPassword &&
          !Validator.confirmPasswordCheck(password, confirmPassword)
        ) {
          setErrMsg((prev) => ({
            ...prev,
            confirmPassword: t(
              "common.messages.error.common.confirmPassword.inValid"
            ),
          }));
        }

        resolve(false);
      }
    });
  };

  // Handle
  const onGenerateOtp = async () => {
    setLoading(true);
    try {
      const payload = {
        username: email,
      };
      const response = await unAuthService(CREATE_OTP_URL, "POST", payload);
      if (response && response.message === "Email has been sent") {
        setLoading(false);
        navigate(verifyEmail, { state: { email } });
      } else {
        setLoading(false);
      }
    } catch {
      setLoading(false);
      toast.error(t("common.messages.notification.failed.serverError"));
    }
  };

  const onSubmitRegistrion = async (e) => {
    e.preventDefault();
    const validationStatus = await getValidation();
    const encryptedPassword = encryptFun(formData.password);
    if (validationStatus) {
      setLoading(true);
      const payload = {
        firstName,
        lastName,
        email,
        password: `${encryptedPassword}`,
        onboarding: false,
      };
      const response = await unAuthService(REGISTRATION_URL, "POST", payload);
      setLoading(false);
      if (response.success) {
        await onGenerateOtp();
      } else {
        if (
          response.message === "Your email id is already exist in the system"
        ) {
          setFormData((prev) => ({
            ...prev,
            isValid: false,
          }));
          setErrMsg((prev) => ({
            ...prev,
            isValid: t(
              "common.messages.error.guest.registration.duplicateEmail"
            ),
          }));
        }
      }
    }
  };

  return (
    <div className="px-lg-4 d-flex flex-column justify-content-center max-w-400 mx-auto">
      <div className="mb-5">
        <h3 className="text-xl-4xl text-2xl font-semibold">
          Create your account
        </h3>
        <div className="text-sm text-grey-600">
          Already have an account?{" "}
          <Link
            to={paths.login}
            className="text-primary-500 text-decoration-none font-medium"
          >
            {t("common.button.signIn")}
          </Link>
        </div>
      </div>
      <div className="new-onbording-form md-max-account-scroll overflow-y-auto px-1">
        <Row className="gx-2">
          <Col sm={12} md={6}>
            <Form.Group className="mb-3" controlId="firstName">
              <Form.Label className="mb-1">
                {t("common.label.firstName")}
              </Form.Label>
              <Form.Control
                size="lg"
                type="text"
                placeholder={t("common.placeHolder.firstName")}
                name="firstName"
                value={firstName}
                onChange={handleChange}
                onKeyDown={handleAlphabets}
                className="rounded-lg"
              />
              {errMsg.firstName && <p className="error">{errMsg.firstName}</p>}
            </Form.Group>
          </Col>
          <Col sm={12} md={6}>
            <Form.Group className="mb-3" controlId="lastName">
              <Form.Label className="mb-1">
                {t("common.label.lastName")}
              </Form.Label>
              <Form.Control
                size="lg"
                type="text"
                placeholder={t("common.placeHolder.lastName")}
                name="lastName"
                value={lastName}
                onChange={handleChange}
                onKeyDown={handleAlphabets}
                className="rounded-lg"
              />
              {errMsg.lastName && <p className="error">{errMsg.lastName}</p>}
            </Form.Group>
          </Col>
          <Col sm={12} md={12}>
            <Form.Group className="mb-3" controlId="formBasicEmail">
              <Form.Label className="mb-1">
                {t("common.label.email")}
              </Form.Label>
              <Form.Control
                size="lg"
                type="email"
                placeholder={t("common.placeHolder.email")}
                name="email"
                value={email}
                onChange={handleChange}
                className="rounded-lg"
              />
              {errMsg.email && <p className="error">{errMsg.email}</p>}
            </Form.Group>
          </Col>
          <Col md={12} sm={12} lg={12}>
            <Form.Group className="mb-4" controlId="password">
              <Form.Label className="mb-1">
                {t("common.label.password")}
              </Form.Label>
              <div className="position-relative">
                <Form.Control
                  size="lg"
                  type={isShowPassword ? "text" : "password"}
                  // placeholder={t("common.placeHolder.password")}
                  placeholder="Enter Password"
                  name="password"
                  value={password}
                  onChange={handleChange}
                  className="form-control form-control-lg pr-10 rounded-lg"
                />
                <button
                  type="button"
                  className="border-start-0 position-absolute top-0 end-0 bottom-0 w-10 d-flex justify-content-center align-items-center text-grey-400 border-0 bg-transparent z-5"
                  onClick={togglePassword}
                >
                  <i
                    className={`bx ${
                      isShowPassword ? "bx-hide" : "bx-show-alt"
                    }`}
                  ></i>
                </button>
              </div>
              {errMsg.password && <p className="error">{errMsg.password}</p>}

              <ul className="list-unstyled text-sm mt-3">
                <li
                  className={`d-flex align-items-center ${
                    passwordStrength.label === "Strong"
                      ? "text-success"
                      : passwordStrength.label === "Medium"
                      ? "text-warning"
                      : passwordStrength.label === "Weak"
                      ? "text-danger"
                      : ""
                  }`}
                >
                  <i className="bx bx-check bx-sm check-icon me-1"></i>
                  <span className="status-text">
                    Password Strength :{" "}
                    <span className="strength-status font-medium">
                      {passwordStrength.label || "Weak"}
                    </span>
                  </span>
                </li>

                {passwordRules.map((rule, idx) => {
                  const isFailed = passwordStrength.errors.includes(rule);
                  return (
                    <li
                      key={idx}
                      className={`d-flex align-items-center ${
                        passwordLength
                          ? isFailed
                            ? "not-matched-text"
                            : "matched-text"
                          : ""
                      }`}
                    >
                      <i
                        className={`bx bx-sm check-icon me-1 ${
                          isFailed ? "bx-x" : "bx-check"
                        }`}
                      ></i>
                      <span className="status-text">{rule}</span>
                    </li>
                  );
                })}
              </ul>
            </Form.Group>
          </Col>
          <Col md={12} sm={12}>
            <Form.Group className="mb-4" controlId="confirmPassword">
              <Form.Label className="mb-1">
                {t("common.label.confirmPassword")}
              </Form.Label>
              <div className="position-relative">
                <Form.Control
                  size="lg"
                  type={isShowConfirmPassword ? "text" : "password"}
                  // placeholder={t("common.placeHolder.confirmPassword")}
                  placeholder="Enter Password"
                  name="confirmPassword"
                  value={confirmPassword}
                  onChange={handleChange}
                  className="form-control form-control-lg pr-10 rounded-lg"
                />
                <button
                  type="button"
                  className="border-start-0 position-absolute top-0 end-0 bottom-0 w-10 d-flex justify-content-center align-items-center text-grey-400 border-0 bg-transparent z-5"
                  onClick={toggleConfirmPassword}
                >
                  <i
                    className={`bx ${
                      isShowConfirmPassword ? "bx-hide" : "bx-show-alt"
                    }`}
                  ></i>
                </button>
              </div>
              {errMsg.confirmPassword && (
                <p className="error">{errMsg.confirmPassword}</p>
              )}
            </Form.Group>
          </Col>
          <Col sm={12} md={12}>
            {!isValid && <Alerts variant="danger" message={errMsg.isValid} />}
          </Col>
          <Col sm={12} md={12}>
            {!loading ? (
              <Button
                variant="primary"
                size="lg"
                onClick={onSubmitRegistrion}
                className="w-100 font-medium rounded-lg"
              >
                {t("common.button.next")}
              </Button>
            ) : (
              <ButtonLoader />
            )}
          </Col>
        </Row>

        <div className="text-center mt-5 text-sm">
          <span className="text-grey-400">
            By signing up to create an account I accept Company’s
            <br />
            <a
              href="#"
              className="text-decoration-none font-semibold text-grey-700"
            >
              Terms of use & Privacy Policy.
            </a>
          </span>
        </div>
      </div>
    </div>
  );
}
